import { useMemo, useState, useCallback } from "react";

import type { CompanyAnalysisCompetititorItem } from "app/pages/sales-intelligence/modules/company-analysis/types/competitors";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

export const useFilteredCompetitors = (competitors?: CompanyAnalysisCompetititorItem[]) => {
    const [searchString, setSearchString] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const handleSearch = useCallback((text: string) => {
        setSearchString(text || "");
    }, []);

    const filteredCompetitors = useMemo(() => {
        const enrichedCompetitors = (competitors || []).map((competitor) => ({
            ...competitor,
            name: competitor.name || competitor.domain,
        }));

        if (!competitors || !searchString) {
            return enrichedCompetitors;
        }
        const queryWords = searchString.trim().toLowerCase().split(/\s+/);
        if (!queryWords.length) {
            return enrichedCompetitors;
        }
        const matchesWord = (text: string) => queryWords.some((word) => text.toLowerCase().includes(word));

        return enrichedCompetitors.filter((competitor) => {
            // The taxonomy is an object, and the scalar sweep below skips objects — without this the
            // industry column would be silently unsearchable for opted-in accounts.
            if (matchesWord(formatTaxonomyPathDisplayNames(competitor.industryTaxonomy?.path))) {
                return true;
            }

            return Object.values(competitor).some((val) => {
                if (is.string(val) || is.number(val) || is.boolean(val)) {
                    return matchesWord(val.toString());
                }
                return false;
            });
        });
    }, [searchString, competitors]);

    return {
        filteredCompetitors,
        currentPage,
        handleSearch,
        setCurrentPage,
    };
};
